import { useCallback, useMemo, useState } from 'react';

import {
  FilterChip,
  FilterEditorComponentProps,
  FilterViewerComponentProps,
  GroupedAutocomplete,
  SearchUtils,
  createFilter,
} from '@spotify/confidence-core-react';

import { LabelDefinition } from '../../../hooks/useLabelsForResource';

export const LABEL_FILTER_ID = 'labels';

export const formatLabelValue = (labelId: string, value: string) =>
  `${labelId}.${value}`;

const parseLabelValue = (
  compositeValue: string,
  labelIds: string[],
): { group: string; item: string } | null => {
  // Label values may themselves contain the `.` separator (e.g. version
  // numbers), so match against the known label ids instead of guessing at
  // a separator position. Prefer the longest id in case one prefixes
  // another.
  const matchingId = labelIds
    .filter(id => compositeValue.startsWith(`${id}.`))
    .sort((a, b) => b.length - a.length)[0];
  if (matchingId && compositeValue.length > matchingId.length + 1) {
    return {
      group: matchingId,
      item: compositeValue.slice(matchingId.length + 1),
    };
  }

  // Composite with an unknown label id — fall back to the last separator.
  const idx = compositeValue.lastIndexOf('.');
  if (idx <= 0 || idx === compositeValue.length - 1) return null;
  return {
    group: compositeValue.slice(0, idx),
    item: compositeValue.slice(idx + 1),
  };
};

const toRecord = (
  flatValues: string[],
  labelIds: string[],
): Record<string, string[]> => {
  const record: Record<string, string[]> = {};
  for (const v of flatValues) {
    const parsed = parseLabelValue(v, labelIds);
    // Skip duplicates so values that accumulated under the old ambiguous
    // parsing self-heal instead of inflating the selection.
    if (parsed && !(record[parsed.group] ?? []).includes(parsed.item)) {
      (record[parsed.group] ??= []).push(parsed.item);
    }
  }
  return record;
};

const toFlat = (record: Record<string, string[]>): string[] =>
  Object.entries(record).flatMap(([group, items]) =>
    items.map(item => formatLabelValue(group, item)),
  );

const GroupedLabelFilterEditor = ({
  value,
  onValueChange,
  labelDefinitions,
  labelIds,
}: FilterEditorComponentProps<string[]> & {
  labelDefinitions: LabelDefinition[];
  labelIds: string[];
}) => {
  const [activeLabel, setActiveLabel] = useState<string | null>(null);

  const groupItems = useMemo(
    () => labelDefinitions.map(d => ({ value: d.id, label: d.displayName })),
    [labelDefinitions],
  );

  const activeDef = labelDefinitions.find(d => d.id === activeLabel);
  const valueItems = useMemo(
    () => (activeDef?.allowedValues ?? []).map(v => ({ value: v })),
    [activeDef],
  );

  const selectedItems = useMemo(
    () => toRecord(value ?? [], labelIds),
    [value, labelIds],
  );
  const handleChange = useCallback(
    (record: Record<string, string[]>) => onValueChange(toFlat(record)),
    [onValueChange],
  );

  return (
    <GroupedAutocomplete.Root
      activeGroup={activeLabel}
      onActiveGroupChange={setActiveLabel}
      selectedItems={selectedItems}
      onSelectedItemsChange={handleChange}
    >
      <GroupedAutocomplete.Groups
        items={groupItems}
        searchPlaceholder="Search labels..."
        emptyMessage="No labels available"
      />
      <GroupedAutocomplete.Items
        items={valueItems}
        searchPlaceholder="Search values..."
        emptyMessage="No values for this label"
      />
    </GroupedAutocomplete.Root>
  );
};

const GroupedLabelFilterViewer = (
  props: FilterViewerComponentProps<string[]> & {
    labelDefinitions: LabelDefinition[];
    labelIds: string[];
  },
) => {
  const { value, labelDefinitions, labelIds } = props;

  const getLabel = () => {
    if (value.length === 0) return 'No labels';
    if (value.length === 1) {
      const parsed = parseLabelValue(value[0], labelIds);
      if (!parsed) return value[0];
      const def = labelDefinitions.find(d => d.id === parsed.group);
      return `${def?.displayName ?? parsed.group}: ${parsed.item}`;
    }
    return `Labels (${value.length})`;
  };

  return <FilterChip {...props} label={getLabel()} />;
};

export const createGroupedLabelFilter = (
  labelDefinitions: LabelDefinition[],
) => {
  const labelIds = labelDefinitions.map(d => d.id);

  return createFilter<string[]>({
    id: LABEL_FILTER_ID,
    label: 'Labels',
    query: (values: string[]) => {
      if (values.length === 0) return '';
      return values
        .map(v => {
          const parsed = parseLabelValue(v, labelIds);
          if (!parsed) return '';
          return `labels.${SearchUtils.escapeQuery(parsed.group)}:"${SearchUtils.escapeQuery(parsed.item)}"`;
        })
        .filter(Boolean)
        .join(' OR ');
    },
    viewer: props => (
      <GroupedLabelFilterViewer
        labelDefinitions={labelDefinitions}
        labelIds={labelIds}
        {...props}
      />
    ),
    editor: props => (
      <GroupedLabelFilterEditor
        labelDefinitions={labelDefinitions}
        labelIds={labelIds}
        {...props}
      />
    ),
  });
};
